
import { Home, Heart, Utensils, Inbox, User, ChefHat, BarChart, ClipboardList } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const BottomNav = () => {
  const location = useLocation();
  const isSellerMode = location.pathname.startsWith('/seller');
  
  // Hide bottom nav on cook signup flow routes and onboarding
  const hideOnRoutes = [
    '/seller/become-cook',
    '/seller/verification/profile',
    '/seller/verification/documents',
    '/seller/verification/quiz',
    '/onboarding'
  ];

  if (hideOnRoutes.includes(location.pathname)) return null;

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/map') return true;
    return location.pathname === path;
  };

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-messages", isSellerMode],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const { data, error } = await supabase
          .from("conversations")
          .select("unread_count")
          .eq(isSellerMode ? "cook_id" : "foodie_id", user.id);

        if (error) {
          console.error("Error fetching unread count:", error);
          return 0;
        }

        // Calculate total unread messages with explicit null check
        const totalUnread = data.reduce((sum, conv) => {
          // Ensure we're adding a number (in case of null or undefined)
          const count = conv.unread_count !== null ? Number(conv.unread_count) : 0;
          return sum + count;
        }, 0);
        
        console.log(`BottomNav: Total unread messages for ${isSellerMode ? 'seller' : 'user'}: ${totalUnread}`);
        return totalUnread;
      } catch (err) {
        console.error("Unexpected error fetching unread count:", err);
        return 0;
      }
    },
    refetchInterval: 3000, // More frequent refresh
    staleTime: 0, // Always consider data stale to force refetching
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });

  if (isSellerMode) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-4 md:hidden">
        <div className="flex justify-between items-center max-w-md mx-auto">
          <Link to="/seller/meals" className="flex flex-col items-center">
            <ChefHat 
              className={`w-6 h-6 ${isActive('/seller/meals') ? 'text-black' : 'text-gray-500'}`} 
            />
            <span className={`text-xs mt-1 ${isActive('/seller/meals') ? 'text-black' : 'text-gray-500'}`}>
              My Meals
            </span>
          </Link>
          <Link to="/seller/orders" className="flex flex-col items-center">
            <ClipboardList 
              className={`w-6 h-6 ${isActive('/seller/orders') ? 'text-black' : 'text-gray-500'}`} 
            />
            <span className={`text-xs mt-1 ${isActive('/seller/orders') ? 'text-black' : 'text-gray-500'}`}>
              Orders
            </span>
          </Link>
          <Link to="/seller/business" className="flex flex-col items-center">
            <BarChart 
              className={`w-6 h-6 ${isActive('/seller/business') ? 'text-black' : 'text-gray-500'}`} 
            />
            <span className={`text-xs mt-1 ${isActive('/seller/business') ? 'text-black' : 'text-gray-500'}`}>
              Business
            </span>
          </Link>
          <Link to="/seller/inbox" className="flex flex-col items-center">
            <div className="relative">
              <Inbox 
                className={`w-6 h-6 ${isActive('/seller/inbox') ? 'text-black' : 'text-gray-500'}`} 
              />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <span className={`text-xs mt-1 ${isActive('/seller/inbox') ? 'text-black' : 'text-gray-500'}`}>
              Inbox
            </span>
          </Link>
          <Link to="/seller/profile" className="flex flex-col items-center">
            <User 
              className={`w-6 h-6 ${isActive('/seller/profile') ? 'text-black' : 'text-gray-500'}`} 
            />
            <span className={`text-xs mt-1 ${isActive('/seller/profile') ? 'text-black' : 'text-gray-500'}`}>
              Me
            </span>
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-4 md:hidden">
      <div className="flex justify-between items-center max-w-md mx-auto">
        <Link to="/" className="flex flex-col items-center">
          <Home 
            className={`w-6 h-6 ${isActive('/') ? 'text-black' : 'text-gray-500'}`} 
          />
          <span className={`text-xs mt-1 ${isActive('/') ? 'text-black' : 'text-gray-500'}`}>
            Home
          </span>
        </Link>
        <Link to="/favorites" className="flex flex-col items-center">
          <Heart 
            className={`w-6 h-6 ${isActive('/favorites') ? 'text-black' : 'text-gray-500'}`} 
          />
          <span className={`text-xs mt-1 ${isActive('/favorites') ? 'text-black' : 'text-gray-500'}`}>
            Favorites
          </span>
        </Link>
        <Link to="/meals" className="flex flex-col items-center">
          <Utensils 
            className={`w-6 h-6 ${isActive('/meals') ? 'text-black' : 'text-gray-500'}`} 
          />
          <span className={`text-xs mt-1 ${isActive('/meals') ? 'text-black' : 'text-gray-500'}`}>
            Meals
          </span>
        </Link>
        <Link to="/inbox" className="flex flex-col items-center">
          <div className="relative">
            <Inbox 
              className={`w-6 h-6 ${isActive('/inbox') ? 'text-black' : 'text-gray-500'}`} 
            />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <span className={`text-xs mt-1 ${isActive('/inbox') ? 'text-black' : 'text-gray-500'}`}>
            Inbox
          </span>
        </Link>
        <Link to="/profile" className="flex flex-col items-center">
          <User 
            className={`w-6 h-6 ${isActive('/profile') ? 'text-black' : 'text-gray-500'}`} 
          />
          <span className={`text-xs mt-1 ${isActive('/profile') ? 'text-black' : 'text-gray-500'}`}>
            Me
          </span>
        </Link>
      </div>
    </nav>
  );
};
